import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Paper,
  Grid,
  Divider,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Checkbox,
} from "@material-ui/core";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";

// Import all step components
import ProductDetails from "./ProductDetails";
import StatusSelection from "./StatusSelection";
import AllocationConfigurations from "./AllocationConfigurations";
import OtherConfigurations from "./OtherConfigurations";
import ProcessFlowConfiguration from "./ProcessFlowConfiguration";
import ShipmentConfigurations from "./ShipmentConfigurations";
import StyledDialog from "../../../../../../common/models/Dialog";
import { appUrl } from "../../../../../../../utils/config";
import { useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";

function normalizeAllocationConfig(backendData, poStatuses) {
  return {
    showExpiredLots: backendData.showExpiredLots === true ? "YES" : "NO",
    defaultAllocationReq: backendData.defaultAllocationReq === true ? "YES" : "NO",
    qtyVarianceAcceptanceLimit: backendData.qtyVarianceAcceptanceLimit?.toString() || "10",
    allowMultipleLotAllocation: backendData.allowMultipleLotAllocation === true ? "YES" : "NO",
    allowOverAllocation: backendData.allowOverAllocation === undefined || backendData.allowOverAllocation === null ? "NA" : backendData.allowOverAllocation === true ? "YES" : "NO",
    overAllocationLimit: backendData.overAllocationLimit === undefined || backendData.overAllocationLimit === null ? "NA" : String(backendData.overAllocationLimit ?? ""),
    allowUnderAllocation: backendData.allowUnderAllocation === undefined || backendData.allowUnderAllocation === null ? "NA" : backendData.allowUnderAllocation === true ? "YES" : "NO",
    underAllocationLimit: backendData.allowUnderAllocationLimit === undefined || backendData.allowUnderAllocationLimit === null ? "NA" : String(backendData.allowUnderAllocationLimit ?? ""),
    holdTimeBtnCreatedAndSWCMO: (backendData.holdTimeBtnCreatedAndSWCMO === undefined || backendData.holdTimeBtnCreatedAndSWCMO === null) ? "NA" : String(backendData.holdTimeBtnCreatedAndSWCMO),
    allowLotAllocationBy: backendData.allowLotAllocationBy === "all" ? "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" : backendData.allowLotAllocationBy || "Sponsor",
    sponsorTriggerStart: backendData.sponsorTriggerStart === undefined || backendData.sponsorTriggerStart === null ? "NA" :
      backendData.sponsorTriggerStart === "poStatus" ? "PO Status" :
        backendData.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    sponsorTriggerEnd: backendData.sponsorTriggerEnd === undefined || backendData.sponsorTriggerEnd === null ? "NA" :
      backendData.sponsorTriggerEnd === "poStatus" ? "PO Status" :
        backendData.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerStart: backendData.cmoTriggerStart === undefined || backendData.cmoTriggerStart === null ? "NA" :
      backendData.cmoTriggerStart === "poStatus" ? "PO Status" :
        backendData.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerEnd: backendData.cmoTriggerEnd === undefined || backendData.cmoTriggerEnd === null ? "NA" :
      backendData.cmoTriggerEnd === "poStatus" ? "PO Status" :
      backendData.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
      "No. of days before expected delivery date",
    sponsorTriggerStartValue: backendData.sponsorTriggerStartValue === undefined || backendData.sponsorTriggerStartValue === null ? "NA" : String(backendData.sponsorTriggerStartValue ?? ""),
    sponsorTriggerEndValue: backendData.sponsorTriggerEndValue === undefined || backendData.sponsorTriggerEndValue === null ? "NA" : String(backendData.sponsorTriggerEndValue ?? ""),
    cmoTriggerStartValue: backendData.cmoTriggerStartValue === undefined || backendData.cmoTriggerStartValue === null ? "NA" : 
        String(backendData.cmoTriggerStartValue ?? ""),
    cmoTriggerEndValue: backendData.cmoTriggerEndValue === undefined || backendData.cmoTriggerEndValue === null ? "NA" : 
        String(backendData.cmoTriggerEndValue ?? ""),
  };
}

function denormalizeAllocationConfig(data, poStatuses) {
  return {
    showExpiredLots: data.showExpiredLots === "YES",
    defaultAllocationReq: data.defaultAllocationReq === "YES",
    qtyVarianceAcceptanceLimit: Number(data.qtyVarianceAcceptanceLimit),
    allowMultipleLotAllocation: data.allowMultipleLotAllocation === "YES",
    allowOverAllocation: data.allowOverAllocation === "NA" ? undefined : data.allowOverAllocation === true, 
    overAllocationLimit: data.overAllocationLimit === "NA" ? undefined : Number(data.overAllocationLimit),
    allowUnderAllocation: data.allowUnderAllocation === "NA" ? undefined : data.allowUnderAllocation === "YES",
    allowUnderAllocationLimit: data.underAllocationLimit === "NA" ? undefined : Number(data.underAllocationLimit),
    holdTimeBtnCreatedAndSWCMO: data.holdTimeBtnCreatedAndSWCMO === "NA" ? undefined : Number(data.holdTimeBtnCreatedAndSWCMO),
    allowLotAllocationBy: data.allowLotAllocationBy === "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" ? "all" : data.allowLotAllocationBy,
    sponsorTriggerStart: data.sponsorTriggerStart === "NA" ? undefined : data.sponsorTriggerStart === "PO Status" ? "poStatus" : data.sponsorTriggerStart === "poStatus" ? "poStatus" : data.sponsorTriggerStart === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore" : data.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    sponsorTriggerEnd: data.sponsorTriggerEnd === "NA" ? undefined : data.sponsorTriggerEnd === "PO Status" ? "poStatus" : data.sponsorTriggerEnd === "poStatus" ? "poStatus" : data.sponsorTriggerEnd === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore" : data.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    cmoTriggerStart: data.cmoTriggerStart === "NA" ? undefined : data.cmoTriggerStart === "PO Status" ? "poStatus" : data.cmoTriggerStart === "poStatus" ? "poStatus" : data.cmoTriggerStart === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore" : data.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    cmoTriggerEnd: data.cmoTriggerEnd === "NA" ? undefined : data.cmoTriggerEnd === "PO Status" ? "poStatus" : data.cmoTriggerEnd === "poStatus" ? "poStatus" : data.cmoTriggerEnd === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore" : data.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    sponsorTriggerStartValue: data.sponsorTriggerStartValue === "NA" ? undefined : (data.sponsorTriggerStart === "poStatus" || data.sponsorTriggerStart === "PO Status") ? (typeof data.sponsorTriggerStartValue === 'number' ? data.sponsorTriggerStartValue : poStatuses?.find((status) => status.status_name === data.sponsorTriggerStartValue)?.status_id) : Number(data.sponsorTriggerStartValue),
    sponsorTriggerEndValue: data.sponsorTriggerEndValue === "NA" ? undefined : (data.sponsorTriggerEnd === "poStatus" || data.sponsorTriggerEnd === "PO Status") ? (typeof data.sponsorTriggerEndValue === 'number' ? data.sponsorTriggerEndValue : poStatuses?.find((status) => status.status_name === data.sponsorTriggerEndValue)?.status_id) : Number(data.sponsorTriggerEndValue),
    cmoTriggerStartValue: data.cmoTriggerStartValue === "NA" ? undefined : (data.cmoTriggerStart === "poStatus" || data.cmoTriggerStart === "PO Status") ? (typeof data.cmoTriggerStartValue === 'number' ? data.cmoTriggerStartValue : poStatuses?.find((status) => status.status_name === data.cmoTriggerStartValue)?.status_id) : Number(data.cmoTriggerStartValue),
    cmoTriggerEndValue: data.cmoTriggerEndValue === "NA" ? undefined : (data.cmoTriggerEnd === "poStatus" || data.cmoTriggerEnd === "PO Status") ? (typeof data.cmoTriggerEndValue === 'number' ? data.cmoTriggerEndValue : poStatuses?.find((status) => status.status_name === data.cmoTriggerEndValue)?.status_id) : Number(data.cmoTriggerEndValue),
  };
}

function transformDocumentDeliverables(rawDocumentsList, collaboratorTypes = [], documentTypes = []) {
  if (!rawDocumentsList || !Array.isArray(rawDocumentsList)) {
    return [];
  }

  return rawDocumentsList.map(doc => {
    const collaborator = collaboratorTypes.find(
      (c) => c.collaborator_type_id === doc.uploadOrgCollaboratorTypeID
    );
    
    // Find the document type to get the newDocumentTypeName if available
    const documentType = documentTypes.find(dt => dt.documentTypeID === doc.documentTypeID);
    const displayDocumentType = documentType?.newDocumentTypeName || documentType?.documentTypeName || doc.documentType;
    
    return {
      documentType: displayDocumentType,
      format: doc.fileFormatType,
      startStatus: doc.startOfUploadStatusText,
      endStatus: doc.endOfUploadStatusText,
      orgResponsibleName: collaborator?.collaborator_type_name || "Unknown",
      mandatory: doc.documentMandatoryFlag
    };
  });
}

// Helper function to check if product is a file uploaded product
function checkFileUploadedProduct(formData, updateModeData) {
  const hasMaterialProducts = (formData?.materialDetails?.productMaterialsList && 
                              formData.materialDetails.productMaterialsList.length > 0) ||
                              (updateModeData?.materialDetails?.productMaterialsList && 
                              updateModeData.materialDetails.productMaterialsList.length > 0);
  const isFgStatusesEmpty = (!formData?.fgStatuses || Object.keys(formData.fgStatuses).length === 0) &&
                           (!formData?.productFGStatuses || Object.keys(formData.productFGStatuses).length === 0) &&
                           (!updateModeData?.productFGStatuses?.fgStatusesJSON || Object.keys(updateModeData.productFGStatuses.fgStatusesJSON).length === 0);
  return hasMaterialProducts && isFgStatusesEmpty;
}

const ReviewAndAddProduct = ({
  formData,
  classes,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  setFormData, updateStepData,
  setActiveStep,
  markApiCallSuccessful
}) => {

  // Create read-only props for the step components
  const readOnlyProps = {
    formData,
    classes,
    // Add a readOnly prop to disable all inputs
    readOnly: true,
  };
  // State to control the success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const location = useLocation();
  const [updateModeData, setUpdateModeData] = useState(null);
  const [error, setError] = useState(null);
  // API base URL - replace with your actual API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);
  const originalSelectedCollabrixID = location.state?.selectedProductFormData?.productDetails?.collabrixProductID || 
                                    location.state?.productDetails?.collabrixProductID || 
                                    formData?.productDetails?.collabrixProductID;


  // Read selectedReplicatedFromProductID from sessionStorage first to check replica mode
  const selectedReplicatedFromProductIDCheck = sessionStorage.getItem("selectedReplicatedFromProductID") || null;

  if (selectedReplicatedFromProductIDCheck) {
    // Only set originalCollabrixProductID when in replica mode
    if (originalSelectedCollabrixID && !sessionStorage.getItem("originalCollabrixProductID")) {
      sessionStorage.setItem("originalCollabrixProductID", originalSelectedCollabrixID.toString());
    }
  }
  
  const storedOriginalID = sessionStorage.getItem("originalCollabrixProductID");
  const finalSelectedCollabrixID = originalSelectedCollabrixID || storedOriginalID;
  

  const selectedCollabrixID = finalSelectedCollabrixID;

  // Read selectedReplicatedFromProductID from sessionStorage
  const [selectedReplicatedFromProductID, setSelectedReplicatedFromProductID] = useState(
    sessionStorage.getItem("selectedReplicatedFromProductID") || ""
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isProductUpdated, setIsProductUpdated] = useState(false);
  const [collaboratorTypes, setCollaboratorTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [allFgStatuses, setAllFgStatuses] = useState(null);

  useEffect(() => {
    setSelectedReplicatedFromProductID(sessionStorage.getItem("selectedReplicatedFromProductID") || "");
  }, []);

  useEffect(() => {
    const callData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch product details, collaborator types, document types, and FG statuses
        const [productResponse, collaboratorResponse, documentTypesResponse, fgStatusesResponse] = await Promise.all([
          axios.get(`${apiBaseUrl}/fetchProductDetails/${!selectedReplicatedFromProductID ? formData?.productDetails?.collabrixProductID : (selectedReplicatedFromProductID || selectedCollabrixID)}`),
          axios.get(`${appUrl}/admin/${localStorage.collabType === "CMO" ? "fetchNetworkTypes" : "fetchPartnerTypes"}/${localStorage.ecosystemId}`),
          axios.get(`${apiBaseUrl}/fetchDocumentTypes/true`),
          axios.get(`${apiBaseUrl}/fetchFGStatuses/true`)
        ]);
        
        // Handle product details response
        if (productResponse.data && productResponse.data.status) {
          const apiData = productResponse.data.data;
          setUpdateModeData(apiData);
        } else {
          setError("Failed to fetch product details.");
        }
        
        // Handle collaborator types response
        if (collaboratorResponse.data && collaboratorResponse.data.status) {
          setCollaboratorTypes(collaboratorResponse.data.data);
        } else {
          setError("Failed to fetch collaborator types.");
        }
        
        // Handle document types response
        if (documentTypesResponse.data && documentTypesResponse.data.status) {
          setDocumentTypes(documentTypesResponse.data.data.documentTypesList || []);
        } else {
          setError("Failed to fetch document types.");
        }
        
        // Handle FG statuses response
        if (fgStatusesResponse.data && fgStatusesResponse.data.status) {
          setAllFgStatuses(fgStatusesResponse.data.data);
        } else {
          setError("Failed to fetch FG statuses.");
        }
        
      } catch (error) {
        setError("An error occurred while fetching data.");
      } finally {
        setIsLoading(false);
      }
    };
    callData();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("productUpdated") === "true") {
      setIsProductUpdated(true);
    }
    return () => {
      // Optionally clear the flag on unmount or navigation
      // sessionStorage.removeItem("productUpdated");
    };
  }, []);
  // Handler for Save button
  const handleSaveClick = async () => {
    try {
      setIsLoading(true);
      
      // Check for the file uploaded product: materialDetails has products AND productFGStatuses is empty
      const isFileUploadedProductFromLocalStorage = localStorage.getItem("fileUploadedProduct") === "true";
      const isFileUploadedProduct = checkFileUploadedProduct(formData, updateModeData) || isFileUploadedProductFromLocalStorage;
      

      const baseProductDetails = selectedReplicatedFromProductID
        ? {
            ...updateModeData?.productDetails, 
            ...formData?.productDetails, 
            collabrixProductID: selectedCollabrixID, 
            replicatedFromProductID: selectedReplicatedFromProductID 
          }
        : updateModeData?.productDetails;
        
      
      // Convert receivingStorageLocationIDsFromSponsorERP from array to comma-separated string if needed
      const processedProductDetails = {
        ...baseProductDetails,
        pendingConfiguration: false,
        receivingStorageLocationIDsFromSponsorERP: (() => {
          const value = baseProductDetails?.receivingStorageLocationIDsFromSponsorERP;
          if (Array.isArray(value) && value.length > 0) {
            return value.join(",");
          }
          return typeof value === 'string' ? value : "";
        })()
      };
      
      // Helper function to get status_id by status_name from fgStatuses
      const getStatusId = (statusName) => {
        // Return 0 for empty or invalid status names
        if (!statusName || statusName === "Not Specified") {
          return 0;
        }

        // If it's already a number, return it as is
        if (typeof statusName === 'number') {
          return statusName;
        }

        // If it's a string that looks like a number, return it as number
        if (typeof statusName === 'string' && !isNaN(statusName) && !isNaN(parseFloat(statusName))) {
          return parseInt(statusName, 10);
        }

        // PO statuses: From formData.fgStatuses (Status Selection step)
        const poStatuses = formData?.fgStatuses?.poStatuses || [];
        
        // Batch statuses: From allFgStatuses (fetchFGStatuses API)
        const batchStatuses = allFgStatuses?.batchStatuses || [];
        
        // All PO statuses from allFgStatuses (fetchFGStatuses API)
        const allPoStatuses = allFgStatuses?.poStatuses || [];
        
        // Also check from updateModeData if available
        const updateModePoStatuses = updateModeData?.productFGStatuses?.fgStatusesJSON?.poStatuses || [];
        const updateModeBatchStatuses = updateModeData?.productFGStatuses?.fgStatusesJSON?.batchStatuses || [];
        
        const allStatuses = [...poStatuses, ...batchStatuses, ...allPoStatuses, ...updateModePoStatuses, ...updateModeBatchStatuses];
        
        // First try exact match
        let found = allStatuses.find(s => s.status_name === statusName);
        
        // If no exact match, try case-insensitive match
        if (!found) {
          found = allStatuses.find(s => 
            s.status_name && 
            s.status_name.toLowerCase().trim() === statusName.toLowerCase().trim()
          );
        }
        
        
        return found ? found.status_id : 0;
      };

      // Helper function to transform document deliverables to API format
      const transformDocumentDeliverablesForAPI = (documentDeliverables) => {
        if (!documentDeliverables || !Array.isArray(documentDeliverables)) return {};
        
        
        const result = documentDeliverables.map(doc => {
          
          // Check if we have the original API data with correct status IDs
          const originalApiDoc = updateModeData?.documentDetailsList?.find(
            apiDoc => apiDoc.documentTypeID === doc.documentTypeID && 
                     apiDoc.fileFormatTypeID === doc.fileFormatTypeID &&
                     apiDoc.uploadOrgCollaboratorTypeID === (doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID)
          );
          
          let startOfUploadStatus, endOfUploadStatus;
          
          if (originalApiDoc) {
            // Use the original API data which has correct status IDs
            startOfUploadStatus = originalApiDoc.startOfUploadStatus === "Not Specified" 
              ? "Not Specified" 
              : String(originalApiDoc.startOfUploadStatus);
            endOfUploadStatus = originalApiDoc.endOfUploadStatus === "Not Specified" 
              ? "Not Specified" 
              : String(originalApiDoc.endOfUploadStatus);
            
          } else {
            // Fallback to status name lookup (existing logic)
            startOfUploadStatus = doc.startStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusId(doc.startStatus));
            endOfUploadStatus = doc.endStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusId(doc.endStatus));
            
          }
          
          const transformed = {
            documentTypeID: doc.documentTypeID,
            fileFormatTypeID: doc.fileFormatTypeID,
            startOfUploadStatus,
            endOfUploadStatus,
            uploadOrgCollaboratorTypeID: doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID,
            documentMandatoryFlag: doc.mandatory !== undefined ? doc.mandatory : (doc.documentMandatoryFlag !== undefined ? doc.documentMandatoryFlag : false),
            orgResponsibleName: doc.orgResponsibleName,
          };
          
          return transformed;
        });
        
        return result;
      };

      const payload = {
        updateProductDetailsFlag: true,
        productDetails: processedProductDetails,
        materialDetails: (formData?.materialDetails && formData.materialDetails.productMaterialsList && formData.materialDetails.productMaterialsList.length > 0) ? formData.materialDetails : (updateModeData?.materialDetails && updateModeData.materialDetails.productMaterialsList && updateModeData.materialDetails.productMaterialsList.length > 0) ? updateModeData.materialDetails : null,
        productFGStatuses: formData?.productFGStatuses || updateModeData?.productFGStatuses || {},
        allocationConfiguration: denormalizeAllocationConfig(formData?.allocationConfiguration || updateModeData?.allocationConfiguration || {}, updateModeData?.productFGStatuses?.fgStatusesJSON?.poStatuses || []),
        otherConfiguration: formData?.otherConfiguration || updateModeData?.otherConfiguration || {},
        processFlowConfiguration: formData?.processFlowConfiguration || updateModeData?.processFlowConfiguration || {},
        documentDetailsList: formData?.documentDeliverables ? transformDocumentDeliverablesForAPI(formData.documentDeliverables) : (updateModeData?.documentDetailsList || {}),
        // shipmentConfiguration: formData?.shipmentConfiguration || updateModeData?.shipmentConfiguration || {},
      };
      
      // For replicated products, remove certain flags; for file uploaded product, always use update flow
      if (selectedReplicatedFromProductID && !isFileUploadedProduct) {
        delete payload.updateProductDetailsFlag;
        delete payload.productDetails.collabrixProductID;
      }


      let response;
      // File uploaded product or existing product: always use updateProductDetails
      if (!selectedReplicatedFromProductID || isFileUploadedProduct) {
        // Add update flags only for file uploaded products
        const updatePayload = isFileUploadedProduct 
          ? {
              ...payload, 
              updateFGStatusesFlag: true, 
              updateProductDetailsFlag: true, 
              updateAllocationConfigFlag: true, 
              updateOtherConfigFlag: true, 
              updateProcessFlowConfigFlag: true, 
              updateDocumentDeliverablesFlag: true
            }
          : payload;

          
        response = await axios.post(`${apiBaseUrl}/updateProductDetails`, updatePayload);

        // Use markApiCallSuccessful for successful updateProductDetails API call
        if (response.data && response.data.status && typeof markApiCallSuccessful === 'function') {
          // Pass false to not navigate automatically, and pass the step name as "Product Details"
          // markApiCallSuccessful(false, null, "Product Details");
            sessionStorage.removeItem('selectionForNewProduct');

        }
      } else {
        // Only for replicated products that are NOT file uploaded products
        response = await axios.post(`${apiBaseUrl}/addproductDetails`, payload);

        // Use markApiCallSuccessful for successful addproductDetails API call
        if (response.data && response.data.status && typeof markApiCallSuccessful === 'function') {
            sessionStorage.removeItem('selectionForNewProduct');

          // Pass false to not navigate automatically, and pass the step name as "Product Details"
          // markApiCallSuccessful(false, null, "Product Details");
        }
      }
      if (response.data && response.data.status) {
        // Set the success popup to show
        setShowSuccessPopup(true);
        setIsProductUpdated(true);
            sessionStorage.removeItem('selectionForNewProduct');

        sessionStorage.setItem("productUpdated", "true");
      } else {
        console.warn("Unexpected response:", response.data);
        sessionStorage.removeItem("productUpdated");
            sessionStorage.removeItem('selectionForNewProduct');

      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsLoading(false);
    }
  };


  // Handler for Ok button in popup
  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);

    // Only after the user has seen and acknowledged the success popup,
    // proceed with navigation by calling handleSubmit
    if (typeof handleSubmit === 'function') {
      handleSubmit();
    }
  };
  // Handler for 'No, change the configurations' button
  const handleNoChangeConfigurations = async () => {
    setIsLoading(true);
    
    // Check for file uploaded product from localStorage
    const isFileUploadedProductFromLocalStorage = localStorage.getItem("fileUploadedProduct") === "true";
    
    // Get product details and convert receivingStorageLocationIDsFromSponsorERP from array to string
    const selectedReplicatedFromProductIDInHandler = sessionStorage.getItem("selectedReplicatedFromProductID");
    const productDetails = selectedReplicatedFromProductIDInHandler
      ? {
          ...updateModeData?.productDetails, 
          ...formData?.productDetails, 
          collabrixProductID: selectedCollabrixID, 
          replicatedFromProductID: selectedReplicatedFromProductIDInHandler 
        }
      : { ...formData?.productDetails };
    if (Array.isArray(productDetails?.receivingStorageLocationIDsFromSponsorERP)) {
      productDetails.receivingStorageLocationIDsFromSponsorERP = 
        productDetails.receivingStorageLocationIDsFromSponsorERP.join(",");
    }
    
    const payload = {
      productDetails: {
        ...productDetails,
        pendingConfiguration: true,
      },
      materialDetails: (formData?.materialDetails && formData.materialDetails.productMaterialsList && formData.materialDetails.productMaterialsList.length > 0) ? formData.materialDetails : (updateModeData?.materialDetails && updateModeData.materialDetails.productMaterialsList && updateModeData.materialDetails.productMaterialsList.length > 0) ? updateModeData.materialDetails : null,
    };
    
    // For file uploaded product, keep collabrixProductID and add updateProductDetailsFlag
    if (isFileUploadedProductFromLocalStorage) {
      payload.updateProductDetailsFlag = true;
    } else {
      delete payload.productDetails.collabrixProductID;
      delete payload.productDetails.replicatedFromProductID;
    }

   
    try {
      let response;
      
      // Use updateProductDetails for file uploaded product, addproductDetails otherwise
      if (isFileUploadedProductFromLocalStorage) {
        response = await axios.post(`${apiBaseUrl}/updateProductDetails`, payload);
      } else {
        response = await axios.post(`${apiBaseUrl}/addproductDetails`, payload);
      }

      // Handle successful API response
      if (response.data && response.data.status) {
        sessionStorage.removeItem('selectionForNewProduct');

        // For file uploaded product, redirect to step 2 (Status Selection)
        if (isFileUploadedProductFromLocalStorage) {
          // Clear file uploaded product flag since we're now going to configure
          localStorage.removeItem("fileUploadedProduct");
          
          // Clear replica-related session storage
          sessionStorage.removeItem("selectedReplicatedFromProductID");
          sessionStorage.removeItem("productUpdated");

          sessionStorage.removeItem("originalCollabrixProductID");
          
          setSelectedReplicatedFromProductID("");
          sessionStorage.setItem("productAddedOnce", "true");
          
          // Set current step to 2 (Status Selection - index 1)
          sessionStorage.setItem("currentStep", "1");
          
          // Use markApiCallSuccessful to show success message and navigate
          if (typeof markApiCallSuccessful === 'function') {
            markApiCallSuccessful(false, null, "Product Details");
          }

          updateStepData("allocationConfiguration", null);
          updateStepData("otherConfiguration", null);
          updateStepData("processFlowConfiguration", null);
          updateStepData("documentDeliverables", null);
          updateStepData("fgStatuses", null);
          
          // Navigate to step 2 (Status Selection)
          if (typeof setActiveStep === "function") {
            setActiveStep(1);
          }
        } else {
          // Regular case - original logic for replica products
          if (typeof markApiCallSuccessful === 'function') {
            markApiCallSuccessful(false, null, "Product Details");
          }

          // Always execute these steps for replica products regardless of response content
          sessionStorage.setItem(`addedProductDetails`, "true");

          // Clear all session storage items
          sessionStorage.removeItem("selectedReplicatedFromProductID");
          sessionStorage.removeItem("productAddedOnce");
          sessionStorage.removeItem("productUpdated");
          sessionStorage.setItem("currentStep", "1");
          sessionStorage.removeItem("originalCollabrixProductID");


          setSelectedReplicatedFromProductID("");
          sessionStorage.setItem("productAddedOnce", "true");
          
          // Use the new product ID from response if available, otherwise use a placeholder or original ID
          const newProductID = response.data.collabrixProductID || selectedCollabrixID;
          const updatedStepData = {
            ...formData?.productDetails,
            collabrixProductID: newProductID,
          };
          
          updateStepData("productDetails", updatedStepData);
          updateStepData("allocationConfiguration", null);
          updateStepData("otherConfiguration", null);
          updateStepData("processFlowConfiguration", null);
          updateStepData("documentDeliverables", null);
          updateStepData("fgStatuses", null);
          
          if (typeof setActiveStep === "function") {
            setActiveStep(1);
          }
        }
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsLoading(false);
    }
  };


  

  // Create a wrapper component to prevent form nesting issues
  const DocumentDeliverablesWrapper = (props) => {
    const { formData, readOnly } = props;

    // Return only the visual part, not the form wrapper
    return (
      <div className="document-deliverables-review">
        <Typography variant="h6">Document Deliverables</Typography>
        {formData.documentDeliverables &&
          formData.documentDeliverables.length > 0 ? (
          <TableContainer style={{
            overflowX: 'auto',
            overflowY: 'auto',
            border: '1px solid #c6c6c6',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
          }}>
            <Table style={{
              minWidth: 650,
              width: '100%',
              tableLayout: 'fixed',
              border: '0px solid #c6c6c6 !important',
            }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className="document-type-col">
                    Document Type
                  </TableCell>
                  <TableCell className="format-col">Format</TableCell>
                  <TableCell className="upload-status-col">
                    Start Of Upload Status
                  </TableCell>
                  <TableCell className="end-upload-status-col">
                    End Of Upload Status
                  </TableCell>
                  <TableCell className="org-doc-col">
                    Org. Responsible For Document
                  </TableCell>
                  <TableCell className="mandatory-docs-col" align="center">
                    Mandatory Document
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.documentDeliverables.map((doc, index) => (
                  <TableRow key={index}>
                    <TableCell style={{ minWidth: 150 }}>
                      <Typography style={{fontSize: 14 }}>  {doc.documentType}</Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 150 }}>
                      <Typography style={{fontSize: 14 }}>  {doc.format}</Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 150 }}>
                      <Typography style={{fontSize: 14 }}>  {doc.startStatus}</Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 150 }}>
                      <Typography style={{fontSize: 14 }}>  {doc.endStatus}</Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 150 }}>
                      <Typography style={{fontSize: 14 }}>  
                        {doc.orgResponsibleName}
                      </Typography>
                    </TableCell>
                                         <TableCell style={{ minWidth: 150 }} align="center">
                       {doc.mandatory === true ? (
                         <Checkbox
                           checked={true}
                           color="primary"
                           disabled={true}
                         />
                       ) : null}
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No document deliverables configured.</Typography>
        )}
      </div>
    );
  };

  const normalizedAllocationConfig = useMemo(() => 
    normalizeAllocationConfig(
      updateModeData?.allocationConfiguration || {},
      updateModeData?.productFGStatuses?.fgStatusesJSON?.poStatuses || []
    ), [updateModeData?.allocationConfiguration, updateModeData?.productFGStatuses?.fgStatusesJSON?.poStatuses]
  );
  
  const normalizedOtherConfig = useMemo(() => 
    updateModeData?.otherConfiguration || {}, [updateModeData?.otherConfiguration]
  );
  
  const normalizedProcessFlowConfig = useMemo(() => 
    updateModeData?.processFlowConfiguration || {}, [updateModeData?.processFlowConfiguration]
  );
  
  const normalizedDocumentDeliverables = useMemo(() => 
    transformDocumentDeliverables(updateModeData?.documentDetailsList, collaboratorTypes, documentTypes), [updateModeData?.documentDetailsList, collaboratorTypes, documentTypes]
  );


  
  return (
    <div className={classes?.formContainer}>
      {/* Full Page Loader */}
      {(isLoading || isRedirecting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}
      <Paper elevation={0} className={`add-flow-view-product-wrapper ${classes.reviewPaper}`}>
        <Grid container spacing={3}>
          {/* Product Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              <ProductDetails formData={updateModeData} {...readOnlyProps} />
            </Box>
          </Grid>
                  <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Status Selection Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              <StatusSelection
                readOnly={true}
                formData={{
                  fgStatuses: updateModeData?.productFGStatuses?.fgStatusesJSON || {}
                }}
              />
            </Box>
          </Grid>
                  <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Allocation Configuration Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              {/* <DocumentDeliverablesWrapper
                formData={formData}
                readOnly={true}
              /> */}
              <AllocationConfigurations
                formData={{
                  ...formData,
                  allocationConfiguration: normalizedAllocationConfig,
                }}
                readOnly={true}
                classes={classes}
              />
            </Box>
          </Grid>
                  <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Other Configurations Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              <OtherConfigurations
                formData={{ ...formData, otherConfiguration: normalizedOtherConfig }}
                readOnly={true}
                classes={classes}
              />
            </Box>
          </Grid>
                  <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Process Flow Configurations Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              <ProcessFlowConfiguration
                formData={{ ...formData, processFlowConfiguration: normalizedProcessFlowConfig }}
                readOnly={true}
                classes={classes}
              />
            </Box>
          </Grid>
                  <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Document Deliverables Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes.reviewSection}>
              <DocumentDeliverablesWrapper
                formData={{ ...formData, documentDeliverables: normalizedDocumentDeliverables }}
                readOnly={true}
                classes={classes}
              />
            </Box>{" "}
          </Grid>

        </Grid>
      </Paper>

      {/* Navigation Buttons */}
      {!selectedReplicatedFromProductID &&
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                className={classes && classes.button}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            className={classes && classes.button}
            disabled={isProductUpdated}
          >
            Save
          </Button>
        </Box>
      }

      {selectedReplicatedFromProductID &&
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}>
          <>
            <Button
              variant="outlined"
              onClick={handleNoChangeConfigurations}
              className={classes && classes.button}
            >
              No, change the configurations
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
          </>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            className={classes && classes.button}
          >
            Yes, confirm the configurations
          </Button>
        </Box>
      }      {/* Success Popup Modal */}
      <Dialog open={showSuccessPopup}
        onClose={handlePopupOk}
        fullWidth
        className="product-added-success-popup"
        maxWidth="sm"
      >
        <StyledDialog
          title="New Product Added"
          subtitle="The product details have been added successfully."
          icon={false} // false means show success (check), true means error (cross)
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>
    </div>
  );
};

export default ReviewAndAddProduct;
